import styled, { css } from 'styled-components';
import { BarTone } from '../../constants';

/**
 * 날짜 열 zebra — 한 칸 건너 옅은 음영.
 *
 * 날짜 행과 막대 레인에 **같은 값**을 깔아야 열이 위아래로 이어져 구획으로 읽힌다.
 * 한쪽만 깔면 어긋난 줄무늬가 되어 오히려 산만해진다.
 */
const CALENDAR_COLUMN_ZEBRA = `repeating-linear-gradient(
  to right,
  rgba(15, 23, 42, 0.018) 0,
  rgba(15, 23, 42, 0.018) calc(100% / 7),
  transparent calc(100% / 7),
  transparent calc(100% * 2 / 7)
)`;

export const StyledCalendarWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const StyledMonthCard = styled.div`
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
`;

/** 상단 전체 접기/펴기 툴바. */
export const StyledCalendarToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** 월 헤더 — 클릭으로 접기/펴기. 접힌 상태에서도 항목 수가 보여야 한다. */
export const StyledMonthTitle = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-bottom: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : '1px solid #e5e7eb')};
  background-color: #f9fafb;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }

  .collapse-caret {
    display: inline-block;
    font-size: 10px;
    color: #6b7280;
    transform: rotate(${({ $isCollapsed }) => ($isCollapsed ? '-90deg' : '0deg')});
    transition: transform 0.12s;
  }

  .month-count {
    margin-left: auto;
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
  }
`;

export const StyledWeekdayRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

/** 요일 헤더 칸. 토=파랑 / 일=빨강 (기존 구글시트 관례). */
export const StyledWeekdayCell = styled.div<{ $color: string }>`
  padding: 6px 0;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  color: ${({ $color }) => $color};
  background-color: rgba(15, 23, 42, 0.105);
  border-right: 1px solid rgba(15, 23, 42, 0.1);
  border-bottom: 1px solid rgba(15, 23, 42, 0.16);

  &:last-child {
    border-right: none;
  }
`;

/**
 * 주 한 줄.
 *
 * 주말 열에 옅은 바탕을 깐다 — 막대가 가로로 이어지면 요일 감각이 금방 사라지는데,
 * 토·일 바탕이 있으면 막대 너머로도 주의 경계가 읽힌다. 간트의 주말 띠와 같은 규약이다.
 */
export const StyledWeekRow = styled.div`
  border-bottom: 3px solid rgba(15, 23, 42, 0.18);
  background-image: linear-gradient(
    to right,
    transparent 0,
    transparent calc(100% * 5 / 7),
    rgba(59, 130, 246, 0.045) calc(100% * 5 / 7),
    rgba(59, 130, 246, 0.045) calc(100% * 6 / 7),
    rgba(239, 68, 68, 0.045) calc(100% * 6 / 7),
    rgba(239, 68, 68, 0.045) 100%
  );

  &:last-child {
    border-bottom: none;
  }
`;

/**
 * 날짜 숫자 행.
 *
 * 주마다 옅은 가로 띠가 생겨 주 단위가 먼저 잡히고, 아래 레인과 같은 zebra 를 깔아
 * **열이 위아래로 이어져** 하나의 날짜 구획으로 읽힌다. 하단선은 날짜 줄과 막대 영역을
 * 갈라 주 하나가 블록으로 보이게 한다.
 */
export const StyledDayNumberRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: rgba(15, 23, 42, 0.062);
  background-image: ${CALENDAR_COLUMN_ZEBRA};
  border-bottom: 1px solid rgba(15, 23, 42, 0.2);
`;

export const StyledDayNumber = styled.div<{
  $isOtherMonth: boolean;
  $isToday: boolean;
  $color: string;
  $background: string;
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: ${({ $isToday }) => ($isToday ? 700 : 500)};
  border-right: 1px solid rgba(15, 23, 42, 0.1);
  white-space: nowrap;
  overflow: hidden;
  /* 다른 달 날짜는 흐리게 — 요일 색보다 "이 달이 아님"이 먼저 읽혀야 한다 */
  color: ${({ $isOtherMonth, $color }) => ($isOtherMonth ? '#d1d5db' : $color)};
  background-color: ${({ $isToday, $background }) => ($isToday ? '#fef3c7' : $background)};

  &:last-child {
    border-right: none;
  }

  .day-number {
    flex: 0 0 auto;
  }

  /**
   * 공휴일명 — 날짜 **오른쪽**에 붙인다.
   *
   * 아래로 내리면 공휴일이 낀 주만 날짜 줄이 2행이 되어 주마다 높이가 달라진다.
   * 가로로 세우고 말줄임을 걸면 어떤 공휴일이 와도 높이가 고정된다. 좁은 칸에서
   * 이름이 잘려도 빨간색만으로 쉬는 날은 읽히고, 전체 이름은 title 툴팁이 맡는다.
   */
  .holiday-memo {
    flex: 0 1 auto;
    min-width: 0;
    font-size: 10px;
    font-weight: 600;
    color: ${({ $isOtherMonth }) => ($isOtherMonth ? '#e5b4b4' : '#dc2626')};
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .holiday-memo::before {
    content: '·\00a0';
    font-weight: 800;
  }
`;

/** 한 주 행 안의 막대 레인. 겹치는 항목 수만큼 행이 늘어난다. */
export const StyledLaneGrid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 6px;
  padding: 3px 0 7px;
  min-height: 30px;
  background-color: rgba(15, 23, 42, 0.024);

  /**
   * 날짜 경계선을 **막대 위에** 얹는다.
   *
   * 셀 배경으로 그리면 여러 날에 걸친 막대가 덮어버려, 막대가 많은 주에서는
   * 어느 날인지 전혀 읽히지 않는다. 오버레이로 올리되 pointer-events 를 꺼
   * 막대 클릭·hover 는 그대로 통과시킨다.
   */
  /**
   * 열 zebra — 날짜를 **선이 아니라 면으로** 나눈다.
   *
   * 선은 막대 위를 지나가며 막대 테두리와 섞여 묻히는데, 음영은 막대 아래에 깔려
   * 10행이 쌓여도 끊기지 않는다. 항목이 빽빽한 캘린더에서 날짜 구분에 가장 크게 듣는다.
   */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: ${CALENDAR_COLUMN_ZEBRA};
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background-image: linear-gradient(to right, rgba(15, 23, 42, 0.1) 1px, transparent 1px);
    background-size: calc(100% / 7) 100%;
  }
`;

export const StyledCalendarBar = styled.button<{
  $startColumn: number;
  $span: number;
  $row: number;
  $tone: BarTone;
  $opacity: number;
  $isClippedStart: boolean;
  $isClippedEnd: boolean;
  $isHovered: boolean;
}>`
  position: relative;
  grid-column: ${({ $startColumn, $span }) => `${$startColumn} / span ${$span}`};
  grid-row: ${({ $row }) => `${$row} / span 1`};
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  height: 22px;
  margin: 0 ${({ $isClippedEnd }) => ($isClippedEnd ? '0' : '2px')} 0
    ${({ $isClippedStart }) => ($isClippedStart ? '0' : '2px')};
  /* 시작이 잘리지 않았으면 좌측 스트라이프 자리를 비운다 */
  padding: 0 7px 0 ${({ $isClippedStart }) => ($isClippedStart ? '7px' : '12px')};
  /**
   * 테두리는 트랙색이 아니라 중립 회색이다.
   *
   * 캘린더는 한 화면에 막대가 수십 개라, 막대마다 배경까지 색을 깔면 격자가 묻혀
   * 날짜 구분이 사라진다. 색은 좌측 스트라이프에만 남기고 면은 흰색으로 비운다.
   * 색 면적은 막대당 5px 로 줄지만 트랙 구분은 오히려 또렷해진다.
   */
  border: 1px solid ${({ $tone }) => $tone.barBorder};
  /* 잘린 쪽은 각을 살려 "이어진다"를 표현한다 (월 경계 연속성) */
  border-radius: ${({ $isClippedStart, $isClippedEnd }) =>
    `${$isClippedStart ? '0' : '4px'} ${$isClippedEnd ? '0' : '4px'} ${$isClippedEnd ? '0' : '4px'} ${
      $isClippedStart ? '0' : '4px'
    }`};
  background-color: #fff;
  box-shadow: 0 1px 1.5px rgba(15, 23, 42, 0.12);
  opacity: ${({ $opacity }) => $opacity};
  color: ${({ $tone }) => $tone.text};
  font-size: 10px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  /**
   * 좌측 스트라이프 — 간트와 같은 규약이다.
   *
   * 월 경계에서 잘린 시작에는 그리지 않는다. 거기 스트라이프를 두면
   * "여기서 시작한다" 로 읽혀 이어짐 표현과 부딪힌다.
   */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${({ $isClippedStart }) => ($isClippedStart ? '0' : '5px')};
    background-color: ${({ $tone }) => $tone.stripe};
  }

  /**
   * hover 는 CSS 가 아니라 **스케줄 id 로 공유한다.**
   *
   * 여러 주에 걸친 항목은 주마다 막대가 잘려 별개 엘리먼트가 된다. CSS :hover 로 두면
   * 마우스가 올라간 주만 칠해져 "이번 주에 끝난다" 로 잘못 읽힌다. 같은 id 를 가진
   * 조각 전부를 한꺼번에 칠해야 어디까지 이어지는 일정인지 보인다.
   */
  ${({ $isHovered, $tone }) =>
    $isHovered &&
    css`
      border-color: ${$tone.border};
      background-color: ${$tone.bg};
    `}

  .clip-mark {
    flex: 0 0 auto;
    opacity: 0.55;
  }

  /**
   * 제목 — 공간이 모자라면 **가장 먼저 줄어든다.**
   *
   * flex 아이템은 min-width 가 auto 라 내용보다 작아지지 않는다. 0 으로 풀어야
   * 말줄임이 걸리고, 그래야 뒤따르는 연결 표식이 밀려 잘리지 않는다.
   */
  .bar-title {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* 담당자 — 제목보다 흐리게. 폭이 모자라면 제목보다 먼저 사라진다 */
  .bar-manager {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 0.58;
    font-weight: 500;
  }

  /* 쿠폰 혜택 — 간트와 같은 보라 칩 */
  .bar-chip {
    flex: 0 0 auto;
    padding: 0 5px;
    border-radius: 3px;
    background-color: #ede9fe;
    color: #6d28d9;
    font-size: 9px;
    font-weight: 800;
  }

  .bar-chip-more {
    flex: 0 0 auto;
    font-size: 9px;
    opacity: 0.5;
  }

  /* 좁은 셀에서도 시각이 먼저 잘리지 않게 고정폭으로 둔다 */
  .bar-time {
    flex: 0 0 auto;
    padding: 0 4px;
    border-radius: 3px;
    background-color: rgba(0, 0, 0, 0.055);
    font-size: 9px;
    font-weight: 700;
  }

  /* 기획전·쿠폰 표식 — 간트 1행 모드와 같은 규약 (형태로 훑는다) */
  .bar-flag {
    flex: 0 0 auto;
  }

  .bar-flag-event {
    font-size: 9px;
    color: #0f766e;
  }

  .bar-flag-coupon {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: #6d28d9;
  }
`;

export const StyledEmpty = styled.div`
  padding: 60px 0;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
`;
